import { z } from 'zod';
import { toIssues } from '../materials/catalog.js';
import type { ParseResult, SchemaIssue } from '../voyage/schemas.js';
import { canonicalDec, decCmp, isDecimalText } from './decimal.js';

/**
 * Yakıt kayıtlarının (açılış, alım, hareket) ortak alan şemaları (ADR 0117 §3, §11). Taslak
 * eksik kaydedilebilir: şema yalnız verilen alanın biçimini denetler, zorunluluk gönderimde
 * (`requiredIssues`). Miktarlar kanonik ondalık metindir; JS sayısı kabul edilmez.
 */

const missing = (input: unknown) => input === undefined || input === null;
export const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');

/** İsteğe bağlı tek satır metin; boş metin `null`. */
export const optText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);

/** İsteğe bağlı çok satırlı metin (açıklama); satır sonları korunur. */
export const optNote = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) => value.normalize('NFC').trim())
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);

export const optId = z
  .uuid('INVALID_ID')
  .nullish()
  .transform((value) => value ?? null);

/** Negatif olmayan kanonik ondalık; `positive` sıfırı da reddeder. */
export const optAmount = (options: { positive?: boolean; max?: string } = {}) =>
  z
    .string(typed('INVALID_DECIMAL'))
    .refine(isDecimalText, { message: 'INVALID_DECIMAL', abort: true })
    .transform(canonicalDec)
    .refine((value) => !value.startsWith('-'), { message: 'NEGATIVE_NOT_ALLOWED', abort: true })
    .refine((value) => !options.positive || value !== '0', { message: 'OUT_OF_RANGE', abort: true })
    .refine((value) => options.max === undefined || decCmp(value, options.max) <= 0, 'OUT_OF_RANGE')
    .nullish()
    .transform((value) => value ?? null);

/** İşaretli kanonik ondalık (düzeltme miktarı, trim): eksi kabul edilir. */
export const optSigned = () =>
  z
    .string(typed('INVALID_DECIMAL'))
    .refine(isDecimalText, { message: 'INVALID_DECIMAL', abort: true })
    .transform(canonicalDec)
    .nullish()
    .transform((value) => value ?? null);

/** Gemi yerel tarih ve saati `YYYY-MM-DDTHH:mm` (gemi saati; UTC'ye yazma yolunda çevrilir). */
export const LOCAL_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
export const optLocalTime = z
  .string(typed('INVALID_TIME'))
  .refine(
    (value) => LOCAL_TIME_PATTERN.test(value) && !Number.isNaN(Date.parse(`${value}:00Z`)),
    'INVALID_TIME',
  )
  .nullish()
  .transform((value) => value ?? null);

export const optDay = z
  .string(typed('INVALID_DATE'))
  .refine(
    (value) => /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value)),
    'INVALID_DATE',
  )
  .nullish()
  .transform((value) => value ?? null);

export const optEnum = <const T extends readonly [string, ...string[]]>(values: T) =>
  z
    .enum(values, typed('INVALID_OPTION'))
    .nullish()
    .transform((value) => value ?? null);

/** Gönderimde boş olmaması gereken alanların sorunları (`REQUIRED`). */
export function requiredIssues<T extends object>(
  value: T,
  keys: readonly (keyof T & string)[],
): SchemaIssue[] {
  return keys
    .filter((key) => {
      const v = value[key] as unknown;
      return v === null || v === undefined || (Array.isArray(v) && v.length === 0);
    })
    .map((path) => ({ path, code: 'REQUIRED' }));
}

/** Şema sonucu; aynı alanın ikinci sorunu atılır. */
export function resultOf<T>(
  result: { success: true; data: T } | { success: false; error: z.ZodError },
): ParseResult<T> {
  return result.success
    ? { ok: true, value: result.data }
    : {
        ok: false,
        issues: toIssues(result.error).filter(
          (issue, index, all) => all.findIndex((i) => i.path === issue.path) === index,
        ),
      };
}

/**
 * Gemi yerel zamanını UTC ISO-8601'e çevirir: yerel − fark (ADR 0065). `offsetMin` geminin o
 * anki saat farkıdır.
 */
export function localToUtc(local: string, offsetMin: number): string {
  return new Date(Date.parse(`${local}:00.000Z`) - offsetMin * 60_000).toISOString();
}

/** UTC anını gemi yerel `YYYY-MM-DDTHH:mm` biçimine çevirir. */
export function utcToLocal(utc: string, offsetMin: number): string {
  return new Date(Date.parse(utc) + offsetMin * 60_000).toISOString().slice(0, 16);
}
